import CheckCirclePurpleIcon from '@components/Icon/CheckCirclePurple';
import { useDebounce } from '@hooks/useDebounce';
import { usePageSize } from '@hooks/usePageSize';
import { useGetJobApplications } from '@modules/jobApplications/hooks/useGetJobApplications';
import { useGetJobApplicationsKanban } from '@modules/jobApplications/hooks/useGetJobApplicationsKanban';
import { defaultJobApplicationFilters } from '@modules/jobApplications/JobApplicationFilter';
import JobApplicationListContainer from '@modules/jobApplications/JobApplicationListContainer';
import { JobApplicationStatus } from '@modules/jobApplications/statuses';
import { Box, styled } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import { Stack } from '@mui/system';
import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

const CustomSnackbar = styled(Snackbar)(({ theme }) => ({
    background: theme.palette.neutralBlack.main,
    borderTopRightRadius: '8px',
    borderTopLeftRadius: '8px',
    padding: '16px',
    borderBottom: `5px solid ${theme.palette.primaryPurple.main}`,

    '& .snackbar-content': {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontWeight: 600,
        fontSize: '16px',
        lineHeight: '20px',
        color: theme.palette.neutralWhite.main,
    },
}));

const CompanyJobApplications = () => {
    const params = useParams<{ id: string }>();
    const { pageSize, storePageSize } = usePageSize({ storageKey: 'company-job-applications-page-size' });

    const [snackbarAction, setSnackbarAction] = useState({
        show: false,
        message: '',
        status: '',
    });
    const SnackbarComponent = <React.Fragment>{snackbarAction.message}</React.Fragment>;

    const [searchParams, setSearchParams] = useState({
        query: '',
        pagination: {
            page: 1,
            limit: pageSize,
        },
        sortBy: { field: 'createdDate', order: -1 },
        filter: {
            ...defaultJobApplicationFilters,
            proId: params.id ? [params.id] : [],
        },
    });

    const debouncedSearchParams = useDebounce(searchParams, 500);

    const {
        jobApplications,
        loading: listLoading,
        isLoadingMore,
        error: listError,
        totalDocs: listTotalDocs,
        totalPages,
        loadMore: loadMoreList,
        forceRefresh,
    } = useGetJobApplications(
        debouncedSearchParams.query,
        debouncedSearchParams.filter,
        debouncedSearchParams.sortBy,
        debouncedSearchParams.pagination.page - 1,
        debouncedSearchParams.pagination.limit,
    );

    const { kanbanData, loadMore } = useGetJobApplicationsKanban(
        debouncedSearchParams.query,
        debouncedSearchParams.filter,
        debouncedSearchParams.sortBy,
        debouncedSearchParams.pagination.limit,
    );

    const handleStatusUpdate = useCallback((applicationId: number, newStatus: JobApplicationStatus) => {
        console.log('Update job application status:', { applicationId, newStatus });
    }, []);

    const onQueryChange = useCallback((value: string) => {
        setSearchParams(prev => ({ ...prev, query: value }));
    }, []);

    if (listError) {
        console.error('Error fetching job applications:', listError);
        return (
            <div>
                Error fetching job applications <pre>{JSON.stringify(listError, null, 2)}</pre>
            </div>
        );
    }

    return (
        <Stack sx={{ pt: 0, flex: 1, minHeight: '100%', gap: 3, width: '100%' }}>
            <CustomSnackbar
                open={snackbarAction.show}
                autoHideDuration={5000}
                onClose={() => setSnackbarAction({ show: false, message: '', status: '' })}
                message={snackbarAction?.status}
                action={SnackbarComponent}>
                <Box className="snackbar-content">
                    <CheckCirclePurpleIcon style={{ width: '20px', height: '20px' }} />
                    {snackbarAction?.message}
                </Box>
            </CustomSnackbar>
            <JobApplicationListContainer
                data={jobApplications}
                kanbanData={kanbanData}
                loading={listLoading}
                isLoadingMore={isLoadingMore}
                totalDocs={listTotalDocs}
                totalPages={totalPages}
                displayMode="talent-focus"
                currentPage={searchParams.pagination.page}
                itemsPerPage={searchParams.pagination.limit}
                onPageChange={page => {
                    setSearchParams(prev => ({
                        ...prev,
                        pagination: { ...prev.pagination, page },
                    }));
                }}
                onItemsPerPageChange={limit => {
                    storePageSize(limit);
                    setSearchParams(prev => ({
                        ...prev,
                        pagination: { page: 1, limit },
                    }));
                }}
                onSortChange={sortBy => {
                    setSearchParams(prev => ({
                        ...prev,
                        sortBy: { field: sortBy.field, order: -1 },
                    }));
                }}
                onQueryChange={onQueryChange}
                onFilterChange={filter => {
                    setSearchParams(prev => ({
                        ...prev,
                        filter: {
                            ...filter,
                            proId: params.id ? [params.id] : [],
                        },
                    }));
                }}
                filter={searchParams.filter}
                showFilter={true}
                showPagination={true}
                onLoadMoreKanban={loadMore}
                onStatusUpdate={handleStatusUpdate}
                hideChipKeys={['proId']}
                onRefresh={forceRefresh}
                onLoadMore={loadMoreList}
                meta={{ totalDocs: listTotalDocs, page: searchParams.pagination.page, limit: searchParams.pagination.limit }}
            />
        </Stack>
    );
};

export default CompanyJobApplications;
